import React, { useEffect, useRef, useState, type FC } from 'react';
import { Icon, ZIndex } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import Modal from './Modal';
import { isNil } from 'lodash';
import { ModalHeader } from './ModalHeader';

interface VideoModalProps {
  onClose: () => void;
  trackHeartbeat: (timeElapsed?: number, timeTotal?: number) => void;
  title?: string;
  isInitiallyMuted?: boolean;
}

const VideoModal: FC<React.PropsWithChildren<VideoModalProps>> = ({
  children,
  isInitiallyMuted = false,
  onClose,
  title,
  trackHeartbeat,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoHeartbeatRef = useRef<number | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!isPlaying) {
      if (!isNil(videoHeartbeatRef.current)) {
        clearInterval(videoHeartbeatRef.current);
        videoHeartbeatRef.current = undefined;
      }
      return;
    }
    trackHeartbeat(videoRef.current?.currentTime, videoRef.current?.duration);
    if (isNil(videoHeartbeatRef.current)) {
      videoHeartbeatRef.current = window.setInterval(trackHeartbeat, 5000);
    }
  }, [isPlaying, trackHeartbeat]);

  useEffect(() => {
    return () => window.clearInterval(videoHeartbeatRef.current);
  }, []);

  const onCloseClick = () => {
    setIsPlaying(false);
    onClose();
  };

  const xButton = (
    <button
      className={cn([
        `absolute right-[16px] top-[20px] w-[24px] h-[24px] z-[${ZIndex.ModalFront}] [&_i]:text-[24px]`,
        title ? '[&_i]:text-venn-black' : '[&_i]:text-venn-primary-main',
      ])}
      type="button"
      onClick={onCloseClick}
    >
      <Icon type="times" prefix="fal" />
    </button>
  );

  return (
    <Modal size={1214} inverted closeOnEsc onClose={onCloseClick} noPadding>
      {title ? (
        <ModalHeader className="bg-venn-white m-0 py-[20px] px-[24px]">
          {title}
          {xButton}
        </ModalHeader>
      ) : (
        xButton
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        className="h-[684px] w-[1212px]"
        ref={(ref) => (videoRef.current = ref)}
        controls
        autoPlay
        muted={isInitiallyMuted}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      >
        {children}
      </video>
    </Modal>
  );
};

export default VideoModal;
